import React, { useState, useEffect } from 'react';
import { Download } from 'lucide-react';
import api from '../../services/api';
import ReportFilters, { getDefaultDateRange } from './ReportFilters';

const SupplierSummaryReport = () => {
  const [data, setData] = useState({ results: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    ...getDefaultDateRange(),
    supplier: '',
    cost_centre: '',
    paid_status: '',
  });

  const fetchData = async (appliedFilters = filters) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (appliedFilters.date_from) params.append('date_from', appliedFilters.date_from);
      if (appliedFilters.date_to) params.append('date_to', appliedFilters.date_to);
      if (appliedFilters.supplier) params.append('supplier', appliedFilters.supplier);
      if (appliedFilters.cost_centre) params.append('cost_centre', appliedFilters.cost_centre);
      if (appliedFilters.paid_status) params.append('paid_status', appliedFilters.paid_status);
      
      const response = await api.get(`/api/reports/supplier-summary/?${params.toString()}`);
      const responseData = response.data;
      setData({
        results: Array.isArray(responseData) ? responseData : (responseData.results || []),
        totals: responseData.totals || {}
      });
    } catch (err) {
      setError('Failed to load report data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    fetchData(filters);
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      ...getDefaultDateRange(),
      supplier: '',
      cost_centre: '',
      paid_status: '',
    };
    setFilters(defaultFilters);
    fetchData(defaultFilters);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.supplier) params.append('supplier', filters.supplier);
      if (filters.cost_centre) params.append('cost_centre', filters.cost_centre);
      if (filters.paid_status) params.append('paid_status', filters.paid_status);
      
      const response = await api.get(`/api/reports/supplier-summary/export/?${params.toString()}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `supplier_summary_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export report');
    }
  };

  const formatCurrency = (amount, currency = '') => {
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : 'R';
    return `${symbol} ${parseFloat(amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="report-container">
      <div className="report-header">
        <h1>Supplier Summary Report</h1>
        <button className="btn btn-primary" onClick={handleExport} disabled={loading}>
          <Download size={18} />
          Export to Excel
        </button>
      </div>

      <div className="card">
        <ReportFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
        />
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="card">
        {/* Summary Stats */}
        <div className="report-stats">
          <div className="stat-card">
            <div className="stat-label">Total Payments</div>
            <div className="stat-value">{data.totals?.total_payments || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Paid</div>
            <div className="stat-value success">{data.totals?.total_paid || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Unpaid</div>
            <div className="stat-value warning">{data.totals?.total_unpaid || 0}</div>
          </div>
        </div>

        {/* Data Table */}
        {loading ? (
          <div className="loading">Loading report data...</div>
        ) : data.results.length === 0 ? (
          <div className="empty-state">No data found for the selected filters</div>
        ) : (
          <div className="table-responsive">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th className="text-center">Currency</th>
                  <th className="text-right">Total Amount</th>
                  <th className="text-center">Payments</th>
                  <th className="text-right">Lowest</th>
                  <th className="text-right">Average</th>
                  <th className="text-right">Highest</th>
                  <th className="text-center">Paid</th>
                  <th className="text-center">Unpaid</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((row, index) => (
                  <tr key={index}>
                    <td>{row.cr_account_name}</td>
                    <td className="text-center">
                      <span className="code-badge">{row.currency}</span>
                    </td>
                    <td className="text-right">{formatCurrency(row.total_amount, row.currency)}</td>
                    <td className="text-center">{row.payment_count}</td>
                    <td className="text-right">{formatCurrency(row.min_payment, row.currency)}</td>
                    <td className="text-right">{formatCurrency(row.avg_payment, row.currency)}</td>
                    <td className="text-right">{formatCurrency(row.max_payment, row.currency)}</td>
                    <td className="text-center">
                      <span className="badge badge-success">{row.paid_count}</span>
                    </td>
                    <td className="text-center">
                      {row.unpaid_count > 0 ? (
                        <span className="badge badge-warning">{row.unpaid_count}</span>
                      ) : (
                        <span className="badge badge-muted">0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>Total</strong></td>
                  <td></td>
                  <td className="text-right text-muted"><em>Mixed currencies</em></td>
                  <td className="text-center"><strong>{data.totals?.total_payments}</strong></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td className="text-center"><strong>{data.totals?.total_paid}</strong></td>
                  <td className="text-center"><strong>{data.totals?.total_unpaid}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierSummaryReport;